<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OT from "@opentok/client";

import { type ConfigCallFormType, type Device } from "../definitions";

import Select, { type Option } from "@/Components/UI/TcSelect.vue";
import TcSwitch from "@/Components/UI/TcSwitch.vue";
import Loader from "@/Components/UI/TcLoader.vue";

import { hasValue } from "@/Utils/Objects";

export interface ConfigCallFormProps {
  devices: Device[];
  config: ConfigCallFormType;
  isLoadingDevices: boolean;
}

const { t } = useI18n();

const props = withDefaults(defineProps<ConfigCallFormProps>(), { isLoadingDevices: false });
const emit = defineEmits<{
  (e: "updateVideo", option: Option<string>): void;
  (e: "updateAudio", option: Option<string>): void;
  (e: "updateOutput", option: Option<string>): void;
  (e: "updateBlur", value: boolean): void;
  (e: "updateMirror", value: boolean): void;
  (e: "updateDevices"): void;
}>();

const videoDevices = computed((): Device[] => {
  return props.devices.filter((device) => device.kind === "videoInput");
});

const audioDevices = computed((): Device[] => {
  return props.devices.filter((device) => device.kind === "audioInput");
});

const audioOutputDevices = computed((): Device[] => {
  return props.devices.filter((device) => device.kind === "audioOutput");
});

const hasSupportForBlur = computed(() => OT.hasMediaProcessorSupport());

const deviceToOptions = (device: Device): Option<string> =>
  ({
    label: device.label,
    value: device.deviceId,
  }) satisfies Option<string>;

const updateVideo = (value?: Option<string>) => {
  if (!hasValue(value)) return;

  emit("updateVideo", value);
};

const updateAudio = (value?: Option<string>) => {
  if (!hasValue(value)) return;

  emit("updateAudio", value);
};

const updateOutput = (value?: Option<string>) => {
  if (!hasValue(value)) return;

  emit("updateOutput", value);
};

const handleChangeBlur = (value: boolean) => {
  emit("updateBlur", value);
};

const handleChangeMirror = (value: boolean) => {
  emit("updateMirror", value);
};
</script>

<template>
  <div class="config_form">
    <Select
      name="select_video_input"
      :modelValue="props.config.videoId"
      :isDisabled="videoDevices.length === 1"
      :options="videoDevices.map(deviceToOptions)"
      :label="t('general.videocall.config.video.input.label')"
      :placeholder="t('general.videocall.config.video.input.placeholder')"
      @change="updateVideo"
    />

    <div class="config_form__video mb-4">
      <div class="config_form__video__mirror" v-if="hasSupportForBlur && hasValue(props.config.mirror)">
        <TcSwitch :modelValue="!!props.config.mirror" name="mirror" @change="handleChangeMirror" />

        <span>{{ t("general.videocall.config.video.mirror.label") }}</span>
      </div>

      <div class="config_form__video__blur" v-if="hasSupportForBlur">
        <TcSwitch :modelValue="!!props.config.blur" name="blur" @change="handleChangeBlur" />
        <span>{{ t("general.videocall.config.video.blur.label") }}</span>
      </div>
    </div>

    <Select
      :modelValue="props.config.audioId"
      name="select_audio_input"
      :options="audioDevices.map(deviceToOptions)"
      :isDisabled="audioDevices.length === 1"
      :label="t('general.videocall.config.audio.input.label')"
      :placeholder="t('general.videocall.config.audio.input.placeholder')"
      :gutterBottom="audioOutputDevices.length > 0"
      @change="updateAudio"
    />

    <Select
      v-if="audioOutputDevices.length > 1"
      :modelValue="props.config.outputId"
      name="select_audio_output"
      :options="audioOutputDevices.map(deviceToOptions)"
      :label="t('general.videocall.config.audio.output.label')"
      :placeholder="t('general.videocall.config.audio.output.placeholder')"
      :isDisabled="audioOutputDevices.length === 1"
      :gutterBottom="false"
      @change="updateOutput"
    />

    <p class="config_form__update mt-4" @click="() => emit('updateDevices')">
      {{ t("general.videocall.config.update_devices") }}
      <Loader v-if="props.isLoadingDevices" class="config_form__update__loader" :size="20" />
    </p>
  </div>
</template>

<style lang="scss">
.config_form {
  &__video {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: $space-4;

    &__blur,
    &__mirror {
      display: flex;
      align-items: center;
      gap: $space-2;
    }
  }

  &__update {
    @include text.general(low, m);
    @include colors.color(content, mid);

    text-decoration: underline;
    display: flex;

    &:hover {
      font-weight: bold;
      cursor: pointer;
    }

    &__loader {
      @include sp.margin(l, 2);

      height: auto !important;
      width: auto !important;
    }
  }
}
</style>
